import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import AdminHeader from "components/Headers/AdminHeader";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_OPTIONS } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { useDispatch, useSelector } from "react-redux";
import {
  getTraineeList,
  editTrainee,
  downloadTraineeQRCode,
} from "action/trainee";
import { traineeList } from "constants/trainee";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { useNavigate } from "react-router-dom";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { TRAINEE_CONFIG } from "components/constant";
import { REGEX } from "constants/CommonConstants";

function TraineeListing() {
  const isMobile = window.innerWidth < 768;
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const rolePath =
    role === "SUPER_ADMIN"
      ? "super-admin"
      : role === "ADMIN"
        ? "admin"
        : role === "CHANNEL_PARTNER"
          ? "channel-partner"
          : "instructor";
  const config = TRAINEE_CONFIG[role] || {};
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const traineeDataList = useSelector((state) => state.trainees.traineeList);

  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchEmail: "",
    searchMobile: "",
    searchStatus: null,
    searchJointerID: "",
    filterParams: [],
  });

  const [alternative, setAlternative] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
  });

  const [isProcessing, setIsProcessing] = useState({
    downloading: false,
    updatingStatus: false,
  });

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchJointerID) {
      filterParams.push({
        field: "UUID",
        condition: "like",
        filterValues: [filterState?.searchJointerID?.trim()],
      });
    }

    if (filterState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    if (filterState.searchName.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filterState.searchName.trim()],
      });
    }

    if (filterState.searchEmail.trim()) {
      filterParams.push({
        field: "Email",
        condition: "like",
        filterValues: [filterState.searchEmail.trim()],
      });
    }

    if (filterState.searchMobile.trim()) {
      filterParams.push({
        field: "Mobile",
        condition: "like",
        filterValues: [filterState.searchMobile.trim()],
      });
    }

    return {
      filters: filterParams.length > 0 ? JSON.stringify(filterParams) : [],
    };
  };

  //trainee List added sr no. for each item
  const formattedTraineeList = traineeDataList?.data?.trainee?.map(
    (item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
            (traineeDataList?.data?.noOfRecordsPerPage ||
              DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
        fullName: `${item?.user?.firstName || ""} ${
          item?.user?.lastName || ""
        }`,
        email: item?.user?.email || "",
        mobileNo: item?.user?.mobile || "",
        gender: item?.gender || "-",
        status: item?.user?.status || "-",
        createdOn: item?.user?.createdOn || "-",
        qrCode: item?.qrCodePath || "-",
        governmentID: item?.user?.governmentID || "-",
        createdBy: item?.user?.createdBy || "-",
      };
    },
  );

  useEffect(() => {
    setAlternative((prev) => ({
      ...prev,
      isLoading: true,
    }));
    getTraineeList(
      dispatch,
      pageNum,
      buildFilterParams(),
      filterState.searchName,
    );
    return () => {
      dispatch({ type: traineeList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (traineeDataList?.code === 200) {
      setAlternative({
        isLoading: false,
        error: "",
        noRecords: traineeDataList?.data?.trainee?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (traineeDataList?.code || traineeDataList?.message) {
      setAlternative({
        isLoading: false,
        error:
          traineeDataList?.message ||
          "Failed to load trainee list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [traineeDataList]);

  const toggleStatus = (data) => {
    const isActive = data.status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";

    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this trainee?`,
      message: `Trainee "${data.fullName}" will be marked as ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          setIsProcessing((prev) => ({ ...prev, updatingStatus: true }));

          const formDataPayload = new FormData();
          formDataPayload.append(
            "trainee",
            JSON.stringify({
              status: newStatus,
            }),
          );

          const res = await editTrainee(data.ID, formDataPayload);

          if (res?.code === 200) {
            notify("success", res?.message || "Status updated successfully");

            // Refresh list
            getTraineeList(
              dispatch,
              pageNum,
              buildFilterParams(),
              filterState.searchName,
            );
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                  .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                  .join(", ")
              : res?.message || "Failed to update status";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong");
        } finally {
          setIsProcessing((prev) => ({ ...prev, updatingStatus: false }));
        }
      },
    });
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 70,
      centerAlign: true,
    },
    // {
    //   Header: "Profile",
    //   accessor: "profile",
    //   Cell: ({ value }) => (
    //     <img src={value} alt="Profile" className="profile-photo" />
    //   ),
    // },
    {
      Header: "Jointer ID",
      accessor: "uUID",
    },
    {
      Header: "Name",
      accessor: "fullName",
      minWidth: 160,
      maxWidth: 250,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Email",
      accessor: "email",
      minWidth: 200,
      maxWidth: 250,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Mobile No.",
      accessor: "mobileNo",
      centerAlign: true,
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
   
    {
      Header: "Organization",
      accessor: "organizationName",
      minWidth: 100,
      maxWidth: 150,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    // {
    //   Header: "Government ID",
    //   accessor: "governmentID",
    //   minWidth: 180,
    //   maxWidth: 250,
    //   Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    // },
    // {
    //   Header: "Login Method",
    //   accessor: "loginMethod",
    // },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
     {
      Header: "Updated By",
      accessor: "createdBy",
      centerAlign:true,
      minWidth: 180,
      maxWidth: 180,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Registered On",
      accessor: "createdOn",
    },
    {
      Header: "Qr code",
      accessor: "qrCode",
      disableSortBy: true,
      Cell: ({ row }) => {
        const id = row.original.ID;
        return (
          <div className="actions-right d-flex justify-content-start">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`download${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.stopPropagation();
                downloadQR(row.original);
              }}
            >
              <i class="fa-solid fa-download"></i>
              <span>Download</span>
            </Button>
          </div>
        );
      },
    },
    {
      Header: "Actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const isActive = row.original.status === "ACTIVE";
        const encodedId = urlEncodedId(row.original.ID);

        return (
          <div className="d-flex">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`view${row.original.ID}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/trainee/${encodedId}/details`
                    : role === "CHANNEL_PARTNER"
                      ? `/channel-partner/trainee/${encodedId}/details`
                      : `/admin/trainee/${encodedId}/details`,
                );
              }}
            >
              <i className="fas fa-eye mr-1 table-action-primary" />
            </Button>
            <UncontrolledTooltip delay={0} target={`view${row.original.ID}`}>
              View
            </UncontrolledTooltip>
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`edit${row.original.ID}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/trainee/edit/${encodedId}`
                    : role === "CHANNEL_PARTNER"
                      ? `/channel-partner/trainee/edit/${encodedId}`
                      : `/admin/trainee/edit/${encodedId}`,
                  {
                    state: { mode: "edit" },
                  },
                );
              }}
            >
              <i className="fa fa-edit mr-1 table-action-info" />
            </Button>

            <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
              Edit
            </UncontrolledTooltip>
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`document${row.original.ID}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/trainee/${encodedId}/details?tab=documents`
                    : role === "CHANNEL_PARTNER"
                      ? `/channel-partner/trainee/${encodedId}/details?tab=documents`
                      : `/admin/trainee/${encodedId}/details?tab=documents`,
                );
              }}
            >
              <i className="fa fa-file-alt mr-1 text-primary" />
            </Button>
            <UncontrolledTooltip
              delay={0}
              target={`document${row.original.ID}`}
            >
              Document
            </UncontrolledTooltip>

            <span className="mr-1 text-muted">|</span>
            <Button
              color="link"
              size="lg"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${row.original.ID}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(row.original);
              }}
            >
              <i
                className={`fas status-toggle-icon ${
                  isActive
                    ? "fa-toggle-on text-success"
                    : "fa-toggle-off text-danger"
                }`}
              />
            </Button>
            <UncontrolledTooltip delay={0} target={`status${row.original.ID}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const downloadQR = async (trainee) => {
    setIsProcessing((prev) => ({ ...prev, downloading: true }));
    try {
      const res = await downloadTraineeQRCode(trainee.ID);

      if (res.status === 200 && res.data) {
        const fileName =
          res.headers["content-disposition"]?.match(/filename="(.+?)"/)?.[1] ||
          "qr-code.png";

        const url = URL.createObjectURL(res.data);

        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      } else if (res.code === 404) {
        notify(
          "danger",
          res?.message ||
            "The QR code for this trainee was not found. Please regenerate it.",
        );
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to download QR code. Please try again.";

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.message || "Failed to download QR code. Please try again.",
      );
    }
    setIsProcessing((prev) => ({ ...prev, downloading: false }));
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const clearSearch = () => {
    setAlternative((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchEmail: "",
      searchMobile: "",
      searchJointerID: "",
      searchPaymentTerm: null,
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    if (
      !filterState.searchName.trim() &&
      !filterState.searchJointerID.trim() &&
      !filterState.searchEmail.trim() &&
      !filterState.searchMobile.trim() &&
      !filterState.searchStatus
    ) {
      return;
    }

    const newfilterParams = buildFilterParams();
    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setAlternative((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleOnRowClick = (rowData) => {
    const encodedId = urlEncodedId(rowData.ID);
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/trainee/${encodedId}/details`
        : role === "CHANNEL_PARTNER"
          ? `/channel-partner/trainee/${encodedId}/details`
          : `/admin/trainee/${encodedId}/details`,
    );
  };

  //role based component display for add
  const handleAdd = () => {
    navigate(`/${rolePath}/trainee/add-trainee`, {
      state: { mode: "add" },
    });
  };
  const handleMobileChange = (e) => {
    const value = e.target.value;
    if (!REGEX.ONLY_DIGITS_REGEX.test(value)) {
      return;
    }
    setFilterState((prev) => ({
      ...prev,
      searchMobile: value,
    }));
  };
  return (
    <>
      <AdminHeader />
      {(isProcessing.downloading || isProcessing.updatingStatus) && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              {isProcessing.downloading
                ? "Downloading… Please wait."
                : isProcessing.updatingStatus
                  ? "Updating status... Please wait."
                  : ""}
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              {/* Header */}
              <CardHeader className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Trainees
                </CardTitle>

                <div className="action-buttons">
                  {/* import trainees btn */}
                  {/* {config.showImportBtn && (
                    <Button
                      className={`btn-deep-blue w-sm-100 w-sm-auto ${isMobile && "mr-0 mb-1"}`}
                    >
                      <i className="fa-solid fa-arrow-right-to-bracket mr-1 fa-lg rotate-90" />
                      Import Trainees
                    </Button>
                  )} */}

                  {config.showAddBtn && (
                    <Button
                      className={`btn-deep-blue w-sm-100 w-sm-auto ${isMobile && "mt-2"}`}
                      onClick={handleAdd}
                    >
                      <i className="fa fa-plus mr-1" />
                      Trainee
                    </Button>
                  )}
                </div>
              </CardHeader>

              {/* Body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by jointer ID"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchJointerID}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchJointerID: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by email"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchEmail}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchEmail: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />
                    <Input
                      type="text"
                      placeholder="Search by mobile no."
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchMobile}
                      onChange={handleMobileChange}
                      onKeyDown={handleKeyDown}
                    />
                    {/* <Select
                      className="mr-2 mb-2 filter-select"
                      options={GENDER_OPTIONS}
                      placeholder="select gender"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      onKeyDown={handleKeyDown}
                    />
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={LOGIN_METHOD}
                      placeholder="Select Method"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      onKeyDown={handleKeyDown}
                    /> */}
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle("15rem")}
                      components={{ Option, SingleValue }}
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${alternative.showLoaderOnClick ? "search-btn" : ""}`}
                      onClick={handleSearch}
                    >
                      {alternative.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams?.filters?.length > 0 && (
                      <Button
                        color="default"
                        className="d-flex align-items-start mb-2 ml-0 filter-btn btn-deep-blue px-4"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1 mt-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {traineeDataList?.data?.totalRecords ? (
                    <>
                      {filterState.filterParams?.filters?.length > 0 ? (
                        <span className="total-records">
                          Showing {traineeDataList?.data?.totalFilteredRecords}{" "}
                          of {traineeDataList?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {traineeDataList?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {alternative.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : alternative.error ? (
                  <>
                    <div className="loaderContainer error-style">
                      <div className="error-icon">
                        <i class="fa-solid fa-circle-exclamation"></i>
                      </div>
                      <span>{alternative.error}</span>
                    </div>
                  </>
                ) : alternative.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i class="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No trainees found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={formattedTraineeList || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        traineeDataList?.data?.totalFilteredRecords /
                          traineeDataList?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                      rowClick={(row) => handleOnRowClick(row)}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default TraineeListing;
